import React, { useEffect, useRef, useState } from 'react';
import './CarruselJuegos.css';
import { useCarruselJuegos } from './useCarruselJuegos';
import { JUEGOS_SIM } from './mecanicasJuegos';
import LeyendaJuegoResponsable from './LeyendaJuegoResponsable';

const CLAVES_CON_SIMULADOR = new Set(JUEGOS_SIM.map((j) => j.clave));
const tieneSimulador = (clave) => CLAVES_CON_SIMULADOR.has(clave);

// El simulador vive en otra sección de la página, así que se lo llama por
// evento en vez de subir el estado hasta la home y bajarlo por props a dos
// ramas que no se conocen entre sí.
const abrirSimulador = (juego) => {
  window.dispatchEvent(new CustomEvent('inpro:abrir-simulador', { detail: { juego } }));
};


/**
 * Carrusel de juegos del hero de la home comercial.
 *
 * Antes acá había una tarjeta fija de Loto Plus con un pozo, un número de
 * sorteo y una fecha de próximo sorteo INVENTADOS —y la fecha ya había pasado
 * hacía dos semanas—. Todo lo que se ve ahora sale de Firestore (N1): si el
 * dato no está, la pieza no se dibuja.
 *
 * Tres estados obligatorios: cargando (esqueleto, cero números), con datos
 * (el carrusel) y sin datos (aviso honesto, también sin un solo número).
 *
 * El conjunto de diapositivas lo deciden los datos, no una lista fija: ver
 * `useCarruselJuegos.js`.
 */

// Un poco más largo que el del tablero (5200 ms): acá hay más para leer en
// cada diapositiva —pozo, próximo sorteo y números—.
const MS_POR_SLIDE = 6200;

const formatoARS = new Intl.NumberFormat('es-AR');

const movimientoReducido = () =>
  typeof window !== 'undefined' &&
  window.matchMedia('(prefers-reduced-motion: reduce)').matches;

/* ─────────────────────────── El monto del pozo ───────────────────────────
   El count-up escribe `textContent` directo, SIN setState por frame: con un
   render por frame el video de fondo del hero se trababa. Se conserva ese
   criterio y solo cambia el objetivo, que ahora es el pozo real.

   El `aria-label` se arma con el valor de verdad. Antes estaba hardcodeado
   ("17.263 millones de pesos"), así que un lector de pantalla anunciaba el
   pozo inventado aunque el texto visible hubiera cambiado. */
function Monto({ valor, texto, activo }) {
  const ref = useRef(null);

  useEffect(() => {
    const nodo = ref.current;
    if (!nodo || typeof valor !== 'number') return undefined;

    const escribir = (n) => { nodo.textContent = `$${formatoARS.format(Math.round(n))}`; };

    // Las diapositivas de atrás siguen en el DOM para el crossfade: ahí el
    // monto se escribe final, sin animar. Y con prefers-reduced-motion,
    // también en la activa.
    if (!activo || movimientoReducido()) {
      escribir(valor);
      return undefined;
    }

    // ~11 actualizaciones por segundo (no 60): el texto cambiando por frame
    // generaba reflows que trababan el video de fondo.
    const DURACION = 1500;
    const PASO_MS = 90;
    const easeOutExpo = (t) => (t === 1 ? 1 : 1 - Math.pow(2, -10 * t));
    let intervalo;
    const inicio = { t: null };
    escribir(0);
    const timer = setTimeout(() => {
      inicio.t = performance.now();
      intervalo = setInterval(() => {
        const p = Math.min((performance.now() - inicio.t) / DURACION, 1);
        escribir(valor * easeOutExpo(p));
        if (p >= 1) clearInterval(intervalo);
      }, PASO_MS);
    }, 160);
    // Red de seguridad: el monto final queda garantizado igual.
    const garantia = setTimeout(() => escribir(valor), 2200);

    return () => {
      clearTimeout(timer);
      clearTimeout(garantia);
      if (intervalo) clearInterval(intervalo);
    };
  }, [valor, activo]);

  if (typeof valor === 'number') {
    return (
      <p className="hc-jp-amount" ref={ref} aria-label={`Pozo estimado: ${formatoARS.format(valor)} pesos`}>
        $0
      </p>
    );
  }

  // El pozo llegó como texto sin normalizar (es el caso del Pozo de la
  // Quiniela). Se muestra tal cual lo publicó la fuente: interpretarlo como
  // número exigiría adivinar una convención de separadores, y equivocarse ahí
  // multiplica un pozo por cien.
  return <p className="hc-jp-amount hc-carr-amount-texto">{texto}</p>;
}

/* ───────────────────────────── Las bolillas ─────────────────────────────
   Solo se renderizan cuando hay números de verdad. En modo `anuncio` este
   bloque no existe: ni casilleros vacíos, ni guiones, ni signos de pregunta.
   Un hueco honesto es mejor que un placeholder que parece un resultado. */
function Bolillas({ numeros, plus, superBola, rotulo }) {
  if (!numeros?.length) return null;

  return (
    <div className="hc-jp-balls" aria-label={rotulo}>
      {numeros.map((n, i) => (
        <span className="hc-ball" key={`${i}-${n}`}>{n}</span>
      ))}
      {plus ? <span className="hc-ball hot" title="Número Plus">{plus}</span> : null}
      {superBola ? <span className="hc-ball hc-ball-super" title="Super Bola">{superBola}</span> : null}
    </div>
  );
}

/* ─────────────────────────── Una diapositiva ───────────────────────────── */
function Lamina({ slide, activa, irASeccion }) {
  const hayPozo = slide.pozoMonto != null || slide.pozoTexto != null;
  const esQuiniela = slide.familia === 'quiniela';

  const meta = [];
  if (slide.turno) meta.push(slide.turno);
  if (slide.modalidad) meta.push(slide.modalidad);
  if (slide.sorteoNro) meta.push(`Sorteo Nº ${slide.sorteoNro}`);
  if (slide.fecha) meta.push(slide.fecha);

  return (
    <article
      className={`hc-carr-lamina${activa ? ' activa' : ''}`}
      aria-hidden={!activa}
      // El vestido entra como variables CSS y sale de `temasJuegos.js`: el
      // componente no sabe qué color es cada juego, solo dónde se aplica.
      style={{
        '--j-acento': slide.acento,
        '--j-tinta': slide.tinta,
        '--j-fuente': slide.fuente || undefined,
        '--j-esc': slide.escLogo,
      }}
    >
      {/* El velo: la luz del juego entrando por detrás del logo. Es el único
          lugar donde se usa el color de marca crudo, y no lleva texto encima
          —por eso no le aplica el mínimo de contraste—. */}
      <span className="hc-carr-velo" aria-hidden="true" />

      <div className="hc-carr-head">
        <span className={`hc-carr-logo${slide.platoLogo ? ' con-plato' : ''}`}>
          <img src={slide.logo} alt={slide.nombre} loading="lazy" />
        </span>
        {slide.etiqueta ? <span className="hc-jp-mod">{slide.etiqueta}</span> : null}
      </div>

      <div className="hc-carr-cuerpo">
        {esQuiniela && slide.cabeza ? (
          <>
            <p className="hc-jp-label">Cabeza · 1<sup>er</sup> premio</p>
            <p className="hc-carr-cabeza">{slide.cabeza}</p>
          </>
        ) : null}

        {hayPozo && !(esQuiniela && slide.cabeza) ? (
          <>
            <p className="hc-jp-label">Pozo estimado</p>
            <Monto valor={slide.pozoMonto} texto={slide.pozoTexto} activo={activa} />
          </>
        ) : null}

        {meta.length ? (
          <p className="hc-jp-meta">
            {meta.map((m) => <span key={m}>{m}</span>)}
          </p>
        ) : null}

        {slide.proximo ? (
          <p className="hc-carr-proximo">
            Próximo sorteo <b>{slide.proximo.cuando} {slide.proximo.fecha}</b>
          </p>
        ) : null}

        {hayPozo && esQuiniela && slide.cabeza ? (
          <p className="hc-carr-proximo">
            Pozo estimado <b>{slide.pozoTexto || `$${formatoARS.format(slide.pozoMonto)}`}</b>
          </p>
        ) : null}

        <Bolillas
          numeros={esQuiniela ? null : slide.numeros}
          plus={slide.plus}
          superBola={slide.superBola}
          rotulo={`Números sorteados de ${slide.nombre}`}
        />

        {slide.caduca ? <p className="hc-carr-nota">Caduca el {slide.caduca}</p> : null}
      </div>

      <div className="hc-jp-foot">
        {/* El destino tiene que ser verdad.
            Antes TODAS las láminas decían "Ver los resultados de hoy" y llevaban
            a #resultados, que es el tablero de la quiniela: al que venía por
            Loto le prometía sus resultados y lo dejaba en Tómbola.
            Ahora: la Tómbola sí va al tablero, porque ese tablero es suyo. Los
            juegos que todavía no tienen extracto publicado en el sitio abren su
            simulador, que existe y explica cómo se juega. Y el que no tiene ni
            una cosa ni la otra no muestra botón, en vez de mandar a cualquier
            lado. Cuando se publiquen los extractos por juego, acá va el sorteo
            puntual. */}
        {slide.juegoClave === 'tombola' ? (
          <a
            href="#resultados"
            onClick={(e) => irASeccion(e, '#resultados')}
            tabIndex={activa ? 0 : -1}
          >
            Ver el extracto completo
            <span aria-hidden="true">→</span>
          </a>
        ) : tieneSimulador(slide.juegoClave) ? (
          <button
            type="button"
            className="hc-jp-foot-btn"
            onClick={() => abrirSimulador(slide.juegoClave)}
            tabIndex={activa ? 0 : -1}
          >
            Cómo se juega
            <span aria-hidden="true">→</span>
          </button>
        ) : <span />}
        <span className="hc-draw-note">Sorteos en vivo por Lotería Jujeña</span>
      </div>
    </article>
  );
}

/* ───────────────────────────── El carrusel ─────────────────────────────── */
export default function CarruselJuegos({ extractos, cargandoExtractos, irASeccion }) {
  const { slides, cargando, clave } = useCarruselJuegos(extractos, cargandoExtractos);
  const [indice, setIndice] = useState(0);
  const [pausado, setPausado] = useState(false);

  // Si cambia el conjunto —llegó un sorteo nuevo, se cayó un juego— el índice
  // vuelve a 0 en vez de quedar apuntando a una posición que ya no existe.
  useEffect(() => { setIndice(0); }, [clave]);

  useEffect(() => {
    if (pausado || slides.length < 2 || movimientoReducido()) return undefined;
    const id = setInterval(() => setIndice((i) => (i + 1) % slides.length), MS_POR_SLIDE);
    return () => clearInterval(id);
  }, [pausado, slides.length]);

  if (cargando) {
    return (
      <aside className="hc-jackpot hc-carr hc-enter d5" aria-label="Pozos y resultados de hoy">
        <div className="hc-carr-caja hc-carr-cargando" aria-busy="true" aria-live="polite">
          <span className="hc-sr-only">Buscando los pozos y los resultados de hoy</span>
          <span className="hc-esqueleto hc-carr-esq-logo" />
          <span className="hc-esqueleto hc-carr-esq-rotulo" />
          <span className="hc-esqueleto hc-carr-esq-monto" />
          <span className="hc-esqueleto hc-carr-esq-linea" />
          <span className="hc-esqueleto hc-carr-esq-bolillas" />
        </div>
        <LeyendaJuegoResponsable variante="franja" />
      </aside>
    );
  }

  // Sin una sola diapositiva no se deja un agujero en el hero: va una tarjeta
  // con el aviso honesto y a dónde ir. Cero números en pantalla.
  if (!slides.length) {
    return (
      <aside className="hc-jackpot hc-carr hc-enter d5" aria-label="Pozos y resultados de hoy">
        <div className="hc-carr-caja hc-carr-sindatos">
          <p className="hc-jp-label">Resultados oficiales</p>
          <h2 className="hc-carr-sindatos-titulo">Todavía no publicamos los pozos ni los resultados de hoy</h2>
          <p className="hc-carr-sindatos-texto">
            Apenas termina cada sorteo, Lotería Jujeña publica el extracto y acá aparece solo.
          </p>
          <div className="hc-jp-foot">
            <a href="#resultados" onClick={(e) => irASeccion(e, '#resultados')}>
              Ir a los resultados <span aria-hidden="true">→</span>
            </a>
            {/* Texto y no link: el dominio oficial lleva ñ y no se verificó
                que resuelva, y un link roto en el hero es peor que la
                referencia escrita. Mismo criterio que el tablero. */}
            <span className="hc-draw-note">loterialajujeña.com.ar</span>
          </div>
        </div>
        <LeyendaJuegoResponsable variante="franja" />
      </aside>
    );
  }

  const activa = Math.min(indice, slides.length - 1);

  return (
    <aside
      className="hc-jackpot hc-carr hc-enter d5"
      aria-roledescription="carrusel"
      aria-label="Pozos y resultados de hoy"
      // El filete del borde inferior y el anillo del logo activo son del
      // marco, no de la lámina: viven fuera del crossfade, así que el color
      // del juego en curso se pasa acá y transiciona por su cuenta.
      style={{
        '--j-acento': slides[activa].acento,
        '--j-tinta': slides[activa].tinta,
      }}
      onMouseEnter={() => setPausado(true)}
      onMouseLeave={() => setPausado(false)}
      onFocusCapture={() => setPausado(true)}
      onBlurCapture={() => setPausado(false)}
      // En pantalla táctil no hay hover: si sigue rotando, el dedo llega
      // cuando ya cambió el juego y se toca el link equivocado. Al primer
      // toque la rotación se detiene y maneja la persona.
      onTouchStart={() => setPausado(true)}
    >
      <div className="hc-carr-caja hc-carr-laminas">
        {slides.map((s, i) => (
          <Lamina key={s.juegoClave} slide={s} activa={i === activa} irASeccion={irASeccion} />
        ))}
      </div>

      {slides.length > 1 && (
        <div className="hc-carr-puntos" role="tablist" aria-label="Elegir juego">
          {slides.map((s, i) => (
            <button
              type="button"
              key={s.juegoClave}
              role="tab"
              aria-selected={i === activa}
              aria-label={s.nombre}
              className={`hc-carr-punto${i === activa ? ' activo' : ''}`}
              onClick={() => setIndice(i)}
            >
              <img src={s.logo} alt="" aria-hidden="true" />
            </button>
          ))}
        </div>
      )}

      {/* P3 · La franja del 10% inferior.

          Va acá, en el marco y no adentro de la lámina, por dos razones: es
          la misma en las seis diapositivas —no es del juego, es de la
          pieza— y así no parpadea con el crossfade de medio segundo.

          Está en los TRES estados del carrusel (cargando, con datos, sin
          datos). Una leyenda que aparece recién cuando llega Firestore no
          es una reserva de grilla, es una decoración. */}
      <LeyendaJuegoResponsable variante="franja" />
    </aside>
  );
}
